import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { apiGet, apiPost, apiPut } from '../utils/api'
import { useParams } from 'react-router-dom'
import { usePerms } from '../auth/PermsContext'
import Confirm from './parts/Confirm'
import { useI18n } from '../i18n'

type Target = { id: number; name: string; type: string; config?: Record<string, any>; include_cache_previews: boolean }

type FieldDef = {
  name: string
  labelKey: string
  placeholderKey?: string
  helperKey?: string
  required?: boolean
  secret?: boolean
  type?: 'text' | 'textarea' | 'select' | 'databases'
  options?: { value: string; labelKey: string }[]
  defaultValue?: string
  trim?: boolean
}

type TargetTypeMeta = {
  labelKey: string
  descriptionKey: string
  fields: FieldDef[]
}

type WebsiteDatabaseForm = {
  type: 'mysql' | 'postgres' | ''
  db_name: string
  db_user: string
  db_password: string
  db_host: string
  db_port: string
  db_socket: string
  dump_options: string
}

const emptyWebsiteDb = (): WebsiteDatabaseForm => ({
  type: 'mysql',
  db_name: '',
  db_user: '',
  db_password: '',
  db_host: 'localhost',
  db_port: '',
  db_socket: '',
  dump_options: '',
})

const TARGET_TYPES: Record<string, TargetTypeMeta> = {
  directory: {
    labelKey: 'target.type.directory',
    descriptionKey: 'target.type.directory.desc',
    fields: [
      { name: 'paths', labelKey: 'target.fields.paths', placeholderKey: 'target.placeholder.paths', required: true, type: 'textarea' },
      { name: 'exclude', labelKey: 'target.fields.exclude', placeholderKey: 'target.placeholder.exclude', type: 'textarea' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  nextcloud: {
    labelKey: 'target.type.nextcloud',
    descriptionKey: 'target.type.nextcloud.desc',
    fields: [
      { name: 'nc_path', labelKey: 'target.fields.nc_path', placeholderKey: 'target.placeholder.nc_path', required: true },
      { name: 'data_path', labelKey: 'target.fields.data_path', placeholderKey: 'target.placeholder.data_path', required: true },
      { name: 'extra_paths', labelKey: 'target.fields.extra_paths', placeholderKey: 'target.placeholder.extra_paths', helperKey: 'target.helper.extra_paths' },
      { name: 'occ_cmd', labelKey: 'target.fields.occ_cmd', placeholderKey: 'target.placeholder.occ_cmd', helperKey: 'target.helper.occ_cmd' },
      { name: 'db_type', labelKey: 'target.fields.db_type', required: true, type: 'select', options: [
        { value: 'mysql', labelKey: 'target.options.db_type.mysql' },
        { value: 'postgres', labelKey: 'target.options.db_type.postgres' },
      ] },
      { name: 'db_host', labelKey: 'target.fields.db_host', placeholderKey: 'target.placeholder.db_host', required: true },
      { name: 'db_port', labelKey: 'target.fields.db_port', placeholderKey: 'target.placeholder.db_port' },
      { name: 'db_socket', labelKey: 'target.fields.db_socket', placeholderKey: 'target.placeholder.db_socket' },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_user', labelKey: 'target.fields.db_user', placeholderKey: 'target.placeholder.db_user', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'exclude_paths', labelKey: 'target.fields.exclude_paths', placeholderKey: 'target.placeholder.exclude_paths' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
    ],
  },
  mysql: {
    labelKey: 'target.type.mysql',
    descriptionKey: 'target.type.mysql.desc',
    fields: [
      { name: 'db_host', labelKey: 'target.fields.db_host', placeholderKey: 'target.placeholder.db_host' },
      { name: 'db_port', labelKey: 'target.fields.db_port', placeholderKey: 'target.placeholder.db_port_mysql' },
      { name: 'db_socket', labelKey: 'target.fields.db_socket', placeholderKey: 'target.placeholder.db_socket_mysql' },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_user', labelKey: 'target.fields.db_user', placeholderKey: 'target.placeholder.db_user', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'dump_options', labelKey: 'target.fields.dump_options', placeholderKey: 'target.placeholder.dump_options', helperKey: 'target.helper.dump_options', type: 'textarea' },
      { name: 'extra_paths', labelKey: 'target.fields.extra_paths', placeholderKey: 'target.placeholder.extra_paths', helperKey: 'target.helper.extra_paths' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  mssql: {
    labelKey: 'target.type.mssql',
    descriptionKey: 'target.type.mssql.desc',
    fields: [
      { name: 'db_host', labelKey: 'target.fields.db_host', placeholderKey: 'target.placeholder.db_host' },
      { name: 'db_port', labelKey: 'target.fields.db_port', placeholderKey: 'target.placeholder.db_port_mssql', defaultValue: '1433' },
      { name: 'db_instance', labelKey: 'target.fields.db_instance', placeholderKey: 'target.placeholder.db_instance', helperKey: 'target.helper.db_instance' },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_user', labelKey: 'target.fields.db_user', placeholderKey: 'target.placeholder.db_user', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'dump_options', labelKey: 'target.fields.dump_options', placeholderKey: 'target.placeholder.dump_options', helperKey: 'target.helper.dump_options', type: 'textarea' },
      { name: 'extra_paths', labelKey: 'target.fields.extra_paths', placeholderKey: 'target.placeholder.extra_paths', helperKey: 'target.helper.extra_paths' },
      {
        name: 'trust_server_certificate',
        labelKey: 'target.fields.trust_server_certificate',
        helperKey: 'target.helper.trust_server_certificate',
        type: 'select',
        options: [
          { value: 'false', labelKey: 'target.options.boolean.false' },
          { value: 'true', labelKey: 'target.options.boolean.true' },
        ],
        defaultValue: 'false',
      },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  postgres: {
    labelKey: 'target.type.postgres',
    descriptionKey: 'target.type.postgres.desc',
    fields: [
      { name: 'db_host', labelKey: 'target.fields.db_host', placeholderKey: 'target.placeholder.db_host' },
      { name: 'db_port', labelKey: 'target.fields.db_port', placeholderKey: 'target.placeholder.db_port_postgres' },
      { name: 'db_socket', labelKey: 'target.fields.db_socket', placeholderKey: 'target.placeholder.db_socket_postgres' },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_user', labelKey: 'target.fields.db_user', placeholderKey: 'target.placeholder.db_user', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'dump_options', labelKey: 'target.fields.dump_options', placeholderKey: 'target.placeholder.dump_options', helperKey: 'target.helper.dump_options', type: 'textarea' },
      { name: 'extra_paths', labelKey: 'target.fields.extra_paths', placeholderKey: 'target.placeholder.extra_paths', helperKey: 'target.helper.extra_paths' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  managed_db: {
    labelKey: 'target.type.managed_db',
    descriptionKey: 'target.type.managed_db.desc',
    fields: [
      {
        name: 'provider',
        labelKey: 'target.fields.managed_provider',
        required: true,
        type: 'select',
        options: [
          { value: 'aws_rds', labelKey: 'target.options.managed_provider.aws_rds' },
          { value: 'gcp_cloudsql', labelKey: 'target.options.managed_provider.gcp_cloudsql' },
        ],
        defaultValue: 'aws_rds',
      },
      {
        name: 'engine',
        labelKey: 'target.fields.db_engine',
        required: true,
        type: 'select',
        options: [
          { value: 'mysql', labelKey: 'target.options.db_type.mysql' },
          { value: 'postgres', labelKey: 'target.options.db_type.postgres' },
        ],
        defaultValue: 'postgres',
      },
      { name: 'instance_id', labelKey: 'target.fields.instance_id', placeholderKey: 'target.placeholder.instance_id', required: true },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_username', labelKey: 'target.fields.db_username', placeholderKey: 'target.placeholder.db_username', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'region', labelKey: 'target.fields.region', placeholderKey: 'target.placeholder.region' },
      { name: 'project_id', labelKey: 'target.fields.project_id', placeholderKey: 'target.placeholder.project_id' },
      { name: 'aws_access_key_id', labelKey: 'target.fields.aws_access_key_id', placeholderKey: 'target.placeholder.aws_access_key_id' },
      { name: 'aws_secret_access_key', labelKey: 'target.fields.aws_secret_access_key', placeholderKey: 'target.placeholder.aws_secret_access_key', secret: true },
      { name: 'aws_session_token', labelKey: 'target.fields.aws_session_token', placeholderKey: 'target.placeholder.aws_session_token', secret: true },
      {
        name: 'gcp_service_account',
        labelKey: 'target.fields.gcp_service_account',
        placeholderKey: 'target.placeholder.gcp_service_account',
        helperKey: 'target.helper.gcp_service_account',
        type: 'textarea',
        secret: true,
        trim: false,
      },
      {
        name: 'use_private_ip',
        labelKey: 'target.fields.use_private_ip',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'false',
      },
      { name: 'dump_options', labelKey: 'target.fields.dump_options', placeholderKey: 'target.placeholder.dump_options', helperKey: 'target.helper.dump_options', type: 'textarea' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  google_workspace: {
    labelKey: 'target.type.google_workspace',
    descriptionKey: 'target.type.google_workspace.desc',
    fields: [
      {
        name: 'mode',
        labelKey: 'target.fields.workspace_mode',
        required: true,
        type: 'select',
        options: [
          { value: 'single', labelKey: 'target.options.workspace_mode.single' },
          { value: 'list', labelKey: 'target.options.workspace_mode.list' },
          { value: 'group', labelKey: 'target.options.workspace_mode.group' },
        ],
        defaultValue: 'single',
      },
      { name: 'primary_user', labelKey: 'target.fields.primary_user', placeholderKey: 'target.placeholder.primary_user' },
      { name: 'user_list', labelKey: 'target.fields.user_list', placeholderKey: 'target.placeholder.user_list', helperKey: 'target.helper.user_list', type: 'textarea', trim: false },
      { name: 'group_email', labelKey: 'target.fields.group_email', placeholderKey: 'target.placeholder.group_email' },
      { name: 'admin_user', labelKey: 'target.fields.admin_user', placeholderKey: 'target.placeholder.admin_user' },
      { name: 'modules', labelKey: 'target.fields.workspace_modules', placeholderKey: 'target.placeholder.workspace_modules', helperKey: 'target.helper.workspace_modules' },
      {
        name: 'service_account_json',
        labelKey: 'target.fields.service_account_json',
        placeholderKey: 'target.placeholder.service_account_json',
        helperKey: 'target.helper.service_account_json',
        type: 'textarea',
        secret: true,
        trim: false,
      },
      {
        name: 'drive_include_shared',
        labelKey: 'target.fields.drive_include_shared',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'true',
        helperKey: 'target.helper.drive_include_shared',
      },
      { name: 'drive_query', labelKey: 'target.fields.drive_query', placeholderKey: 'target.placeholder.drive_query', helperKey: 'target.helper.drive_query' },
      { name: 'drive_max_files', labelKey: 'target.fields.drive_max_files', placeholderKey: 'target.placeholder.drive_max_files' },
      { name: 'gmail_query', labelKey: 'target.fields.gmail_query', placeholderKey: 'target.placeholder.gmail_query', helperKey: 'target.helper.gmail_query' },
      { name: 'gmail_label', labelKey: 'target.fields.gmail_label', placeholderKey: 'target.placeholder.gmail_label' },
      { name: 'gmail_max_messages', labelKey: 'target.fields.gmail_max_messages', placeholderKey: 'target.placeholder.gmail_max_messages' },
      { name: 'calendar_time_min', labelKey: 'target.fields.calendar_time_min', placeholderKey: 'target.placeholder.calendar_time_min', helperKey: 'target.helper.calendar_time' },
      { name: 'calendar_time_max', labelKey: 'target.fields.calendar_time_max', placeholderKey: 'target.placeholder.calendar_time_max', helperKey: 'target.helper.calendar_time' },
      { name: 'calendar_max_events', labelKey: 'target.fields.calendar_max_events', placeholderKey: 'target.placeholder.calendar_max_events' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir', defaultValue: '/tmp' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  m365_exchange: {
    labelKey: 'target.type.m365_exchange',
    descriptionKey: 'target.type.m365_exchange.desc',
    fields: [
      { name: 'tenant_id', labelKey: 'target.fields.tenant_id', placeholderKey: 'target.placeholder.tenant_id', required: true },
      { name: 'client_id', labelKey: 'target.fields.client_id', placeholderKey: 'target.placeholder.client_id', required: true },
      { name: 'client_secret', labelKey: 'target.fields.client_secret', placeholderKey: 'target.placeholder.client_secret', required: true, secret: true },
      {
        name: 'mode',
        labelKey: 'target.fields.exchange_mode',
        required: true,
        type: 'select',
        options: [
          { value: 'single', labelKey: 'target.options.workspace_mode.single' },
          { value: 'list', labelKey: 'target.options.workspace_mode.list' },
          { value: 'group', labelKey: 'target.options.workspace_mode.group' },
        ],
        defaultValue: 'single',
      },
      { name: 'primary_user', labelKey: 'target.fields.primary_user', placeholderKey: 'target.placeholder.primary_user' },
      { name: 'user_list', labelKey: 'target.fields.user_list', placeholderKey: 'target.placeholder.user_list', helperKey: 'target.helper.user_list', type: 'textarea', trim: false },
      { name: 'group_email', labelKey: 'target.fields.group_email', placeholderKey: 'target.placeholder.group_email' },
      { name: 'admin_user', labelKey: 'target.fields.admin_user', placeholderKey: 'target.placeholder.admin_user' },
      { name: 'modules', labelKey: 'target.fields.exchange_modules', placeholderKey: 'target.placeholder.exchange_modules', helperKey: 'target.helper.exchange_modules' },
  { name: 'scopes', labelKey: 'target.fields.exchange_scopes', placeholderKey: 'target.placeholder.exchange_scopes', helperKey: 'target.helper.exchange_scopes', type: 'textarea', trim: false },
      { name: 'authority_host', labelKey: 'target.fields.authority_host', placeholderKey: 'target.placeholder.authority_host', helperKey: 'target.helper.authority_host' },
      { name: 'mail_query', labelKey: 'target.fields.mail_query', placeholderKey: 'target.placeholder.mail_query', helperKey: 'target.helper.mail_query' },
      { name: 'calendar_query', labelKey: 'target.fields.calendar_query', placeholderKey: 'target.placeholder.calendar_query', helperKey: 'target.helper.calendar_query' },
      { name: 'max_parallel_exports', labelKey: 'target.fields.max_parallel_exports', placeholderKey: 'target.placeholder.max_parallel_exports' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir', defaultValue: '/tmp' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  m365_onedrive: {
    labelKey: 'target.type.m365_onedrive',
    descriptionKey: 'target.type.m365_onedrive.desc',
    fields: [
      { name: 'tenant_id', labelKey: 'target.fields.tenant_id', placeholderKey: 'target.placeholder.tenant_id', required: true },
      { name: 'client_id', labelKey: 'target.fields.client_id', placeholderKey: 'target.placeholder.client_id', required: true },
      { name: 'client_secret', labelKey: 'target.fields.client_secret', placeholderKey: 'target.placeholder.client_secret', required: true, secret: true },
      {
        name: 'mode',
        labelKey: 'target.fields.workspace_mode',
        required: true,
        type: 'select',
        options: [
          { value: 'single', labelKey: 'target.options.workspace_mode.single' },
          { value: 'list', labelKey: 'target.options.workspace_mode.list' },
          { value: 'group', labelKey: 'target.options.workspace_mode.group' },
        ],
        defaultValue: 'single',
      },
      { name: 'primary_user', labelKey: 'target.fields.primary_user', placeholderKey: 'target.placeholder.primary_user' },
      { name: 'user_list', labelKey: 'target.fields.user_list', placeholderKey: 'target.placeholder.user_list', helperKey: 'target.helper.user_list', type: 'textarea', trim: false },
      { name: 'group_email', labelKey: 'target.fields.group_email', placeholderKey: 'target.placeholder.group_email' },
      { name: 'admin_user', labelKey: 'target.fields.admin_user', placeholderKey: 'target.placeholder.admin_user' },
      {
        name: 'include_shared',
        labelKey: 'target.fields.drive_include_shared',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'true',
        helperKey: 'target.helper.drive_include_shared',
      },
      { name: 'drive_query', labelKey: 'target.fields.drive_query', placeholderKey: 'target.placeholder.drive_query', helperKey: 'target.helper.drive_query' },
      { name: 'max_files', labelKey: 'target.fields.onedrive_max_files', placeholderKey: 'target.placeholder.onedrive_max_files', helperKey: 'target.helper.onedrive_max_files' },
      { name: 'max_bytes', labelKey: 'target.fields.onedrive_max_bytes', placeholderKey: 'target.placeholder.onedrive_max_bytes', helperKey: 'target.helper.onedrive_max_bytes' },
      { name: 'exclude_extensions', labelKey: 'target.fields.onedrive_exclude_extensions', placeholderKey: 'target.placeholder.onedrive_exclude_extensions', helperKey: 'target.helper.onedrive_exclude_extensions', type: 'textarea', trim: false },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir', defaultValue: '/tmp' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  m365_sharepoint: {
    labelKey: 'target.type.m365_sharepoint',
    descriptionKey: 'target.type.m365_sharepoint.desc',
    fields: [
      { name: 'tenant_id', labelKey: 'target.fields.tenant_id', placeholderKey: 'target.placeholder.tenant_id', required: true },
      { name: 'client_id', labelKey: 'target.fields.client_id', placeholderKey: 'target.placeholder.client_id', required: true },
      { name: 'client_secret', labelKey: 'target.fields.client_secret', placeholderKey: 'target.placeholder.client_secret', required: true, secret: true },
      { name: 'sites', labelKey: 'target.fields.sharepoint_sites', placeholderKey: 'target.placeholder.sharepoint_sites', helperKey: 'target.helper.sharepoint_sites', type: 'textarea' },
      { name: 'site_list', labelKey: 'target.fields.sharepoint_site_list', helperKey: 'target.helper.sharepoint_site_list', type: 'textarea', trim: false },
      {
        name: 'include_versions',
        labelKey: 'target.fields.sharepoint_include_versions',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'true',
      },
      { name: 'max_versions', labelKey: 'target.fields.sharepoint_max_versions' },
      { name: 'exclude_paths', labelKey: 'target.fields.sharepoint_exclude_paths', placeholderKey: 'target.placeholder.sharepoint_exclude_paths', type: 'textarea' },
      { name: 'include_lists', labelKey: 'target.fields.sharepoint_include_lists', placeholderKey: 'target.placeholder.sharepoint_include_lists', type: 'textarea' },
      { name: 'library_query', labelKey: 'target.fields.sharepoint_library_query', placeholderKey: 'target.placeholder.sharepoint_library_query' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir', defaultValue: '/tmp' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  m365_teams: {
    labelKey: 'target.type.m365_teams',
    descriptionKey: 'target.type.m365_teams.desc',
    fields: [
      { name: 'tenant_id', labelKey: 'target.fields.tenant_id', placeholderKey: 'target.placeholder.tenant_id', required: true },
      { name: 'client_id', labelKey: 'target.fields.client_id', placeholderKey: 'target.placeholder.client_id', required: true },
      { name: 'client_secret', labelKey: 'target.fields.client_secret', placeholderKey: 'target.placeholder.client_secret', required: true, secret: true },
      { name: 'teams', labelKey: 'target.fields.teams', placeholderKey: 'target.placeholder.teams', type: 'textarea' },
      { name: 'team_list', labelKey: 'target.fields.team_list', helperKey: 'target.helper.team_list', type: 'textarea', trim: false },
      {
        name: 'include_private_channels',
        labelKey: 'target.fields.include_private_channels',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'true',
      },
      {
        name: 'include_chats',
        labelKey: 'target.fields.include_chats',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'false',
      },
      {
        name: 'include_meetings',
        labelKey: 'target.fields.include_meetings',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'true',
      },
      { name: 'chat_user_list', labelKey: 'target.fields.chat_user_list', placeholderKey: 'target.placeholder.chat_user_list', type: 'textarea' },
      { name: 'max_messages', labelKey: 'target.fields.max_messages', placeholderKey: 'target.placeholder.max_messages' },
      { name: 'max_days', labelKey: 'target.fields.max_days', placeholderKey: 'target.placeholder.max_days' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir', defaultValue: '/tmp' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  s3: {
    labelKey: 'target.type.s3',
    descriptionKey: 'target.type.s3.desc',
    fields: [
      { name: 'bucket', labelKey: 'target.fields.bucket', placeholderKey: 'target.placeholder.bucket', required: true },
      { name: 'prefix', labelKey: 'target.fields.prefix', placeholderKey: 'target.placeholder.prefix', helperKey: 'target.helper.s3_prefix' },
      { name: 'region', labelKey: 'target.fields.region', placeholderKey: 'target.placeholder.region' },
      { name: 'endpoint', labelKey: 'target.fields.endpoint', placeholderKey: 'target.placeholder.endpoint', helperKey: 'target.helper.s3_endpoint' },
      { name: 'aws_access_key_id', labelKey: 'target.fields.aws_access_key_id', placeholderKey: 'target.placeholder.aws_access_key_id' },
      { name: 'aws_secret_access_key', labelKey: 'target.fields.aws_secret_access_key', placeholderKey: 'target.placeholder.aws_secret_access_key', secret: true },
      { name: 'aws_session_token', labelKey: 'target.fields.aws_session_token', placeholderKey: 'target.placeholder.aws_session_token', secret: true },
      {
        name: 'include_glacier',
        labelKey: 'target.fields.include_glacier',
        type: 'select',
        options: [
          { value: 'false', labelKey: 'target.options.boolean.false' },
          { value: 'true', labelKey: 'target.options.boolean.true' },
        ],
        defaultValue: 'false',
        helperKey: 'target.helper.include_glacier',
      },
      {
        name: 'force_path_style',
        labelKey: 'target.fields.force_path_style',
        type: 'select',
        options: [
          { value: 'false', labelKey: 'target.options.boolean.false' },
          { value: 'true', labelKey: 'target.options.boolean.true' },
        ],
        defaultValue: 'false',
        helperKey: 'target.helper.force_path_style',
      },
      {
        name: 'use_accelerate_endpoint',
        labelKey: 'target.fields.use_accelerate_endpoint',
        type: 'select',
        options: [
          { value: 'false', labelKey: 'target.options.boolean.false' },
          { value: 'true', labelKey: 'target.options.boolean.true' },
        ],
        defaultValue: 'false',
        helperKey: 'target.helper.use_accelerate_endpoint',
      },
      { name: 'object_limit', labelKey: 'target.fields.object_limit', placeholderKey: 'target.placeholder.object_limit', helperKey: 'target.helper.object_limit' },
      { name: 'max_total_bytes', labelKey: 'target.fields.max_total_bytes', placeholderKey: 'target.placeholder.max_total_bytes', helperKey: 'target.helper.max_total_bytes' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  mongodb: {
    labelKey: 'target.type.mongodb',
    descriptionKey: 'target.type.mongodb.desc',
    fields: [
      { name: 'mongo_uri', labelKey: 'target.fields.mongo_uri', placeholderKey: 'target.placeholder.mongo_uri', required: true, secret: true, type: 'textarea' },
      { name: 'mongo_db', labelKey: 'target.fields.mongo_db', placeholderKey: 'target.placeholder.mongo_db' },
      { name: 'dump_args', labelKey: 'target.fields.dump_args', placeholderKey: 'target.placeholder.dump_args', helperKey: 'target.helper.dump_args', type: 'textarea' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  git: {
    labelKey: 'target.type.git',
    descriptionKey: 'target.type.git.desc',
    fields: [
      { name: 'remote_url', labelKey: 'target.fields.git_remote_url', placeholderKey: 'target.placeholder.git_remote_url', required: true },
      {
        name: 'auth_method',
        labelKey: 'target.fields.git_auth_method',
        type: 'select',
        options: [
          { value: 'auto', labelKey: 'target.options.git_auth.auto' },
          { value: 'none', labelKey: 'target.options.git_auth.none' },
          { value: 'token', labelKey: 'target.options.git_auth.token' },
          { value: 'basic', labelKey: 'target.options.git_auth.basic' },
          { value: 'ssh', labelKey: 'target.options.git_auth.ssh' },
        ],
        defaultValue: 'auto',
        helperKey: 'target.helper.git_auth_method',
      },
      { name: 'token_username', labelKey: 'target.fields.git_token_username', placeholderKey: 'target.placeholder.git_token_username', helperKey: 'target.helper.git_token_username' },
      { name: 'token', labelKey: 'target.fields.git_token', placeholderKey: 'target.placeholder.git_token', secret: true, helperKey: 'target.helper.git_token' },
      { name: 'username', labelKey: 'target.fields.git_username', placeholderKey: 'target.placeholder.git_username', helperKey: 'target.helper.git_basic' },
      { name: 'password', labelKey: 'target.fields.git_password', placeholderKey: 'target.placeholder.git_password', secret: true, helperKey: 'target.helper.git_basic' },
      { name: 'ssh_private_key', labelKey: 'target.fields.git_ssh_private_key', placeholderKey: 'target.placeholder.git_ssh_private_key', type: 'textarea', helperKey: 'target.helper.git_ssh_private_key', trim: false },
      { name: 'ssh_known_hosts', labelKey: 'target.fields.git_ssh_known_hosts', placeholderKey: 'target.placeholder.git_ssh_known_hosts', type: 'textarea', helperKey: 'target.helper.git_known_hosts', trim: false },
      {
        name: 'mirror',
        labelKey: 'target.fields.git_mirror',
        type: 'select',
        options: [
          { value: 'true', labelKey: 'target.options.boolean.true' },
          { value: 'false', labelKey: 'target.options.boolean.false' },
        ],
        defaultValue: 'true',
        helperKey: 'target.helper.git_mirror',
      },
      { name: 'depth', labelKey: 'target.fields.git_depth', placeholderKey: 'target.placeholder.git_depth', helperKey: 'target.helper.git_depth' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  website: {
    labelKey: 'target.type.website',
    descriptionKey: 'target.type.website.desc',
    fields: [
      { name: 'paths', labelKey: 'target.fields.paths', placeholderKey: 'target.placeholder.paths', required: true, type: 'textarea' },
      { name: 'extra_paths', labelKey: 'target.fields.extra_paths', placeholderKey: 'target.placeholder.extra_paths', helperKey: 'target.helper.extra_paths', type: 'textarea' },
      { name: 'exclude_paths', labelKey: 'target.fields.exclude_paths', placeholderKey: 'target.placeholder.exclude_paths', type: 'textarea' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'maintenance_enabled', labelKey: 'target.fields.maintenance_enabled', type: 'select', options: [
        { value: 'true', labelKey: 'target.options.boolean.true' },
        { value: 'false', labelKey: 'target.options.boolean.false' },
      ] },
      { name: 'maintenance_on_cmd', labelKey: 'target.fields.maintenance_on_cmd', placeholderKey: 'target.placeholder.maintenance_on_cmd', helperKey: 'target.helper.maintenance_on_cmd' },
    { name: 'maintenance_off_cmd', labelKey: 'target.fields.maintenance_off_cmd', placeholderKey: 'target.placeholder.maintenance_off_cmd', helperKey: 'target.helper.maintenance_off_cmd' },
    { name: 'databases', labelKey: 'target.fields.databases', helperKey: 'target.website.database_hint', type: 'databases' },
      { name: 'db_type', labelKey: 'target.fields.db_type', required: true, type: 'select', options: [
        { value: 'mysql', labelKey: 'target.options.db_type.mysql' },
        { value: 'postgres', labelKey: 'target.options.db_type.postgres' },
      ] },
      { name: 'db_host', labelKey: 'target.fields.db_host', placeholderKey: 'target.placeholder.db_host' },
      { name: 'db_port', labelKey: 'target.fields.db_port', placeholderKey: 'target.placeholder.db_port' },
      { name: 'db_socket', labelKey: 'target.fields.db_socket', placeholderKey: 'target.placeholder.db_socket' },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_user', labelKey: 'target.fields.db_user', placeholderKey: 'target.placeholder.db_user', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'db_dump_options', labelKey: 'target.fields.dump_options', placeholderKey: 'target.placeholder.dump_options', helperKey: 'target.helper.dump_options', type: 'textarea' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  imap: {
    labelKey: 'target.type.imap',
    descriptionKey: 'target.type.imap.desc',
    fields: [
      { name: 'host', labelKey: 'target.fields.imap_host', placeholderKey: 'target.placeholder.imap_host', required: true },
      { name: 'port', labelKey: 'target.fields.imap_port', placeholderKey: 'target.placeholder.imap_port' },
      { name: 'use_ssl', labelKey: 'target.fields.imap_use_ssl', type: 'select', options: [
        { value: 'true', labelKey: 'target.options.boolean.true' },
        { value: 'false', labelKey: 'target.options.boolean.false' },
      ] },
      { name: 'username', labelKey: 'target.fields.imap_username', placeholderKey: 'target.placeholder.imap_username', required: true },
      { name: 'password', labelKey: 'target.fields.imap_password', placeholderKey: 'target.placeholder.imap_password', required: true, secret: true },
      { name: 'mailboxes', labelKey: 'target.fields.imap_mailboxes', placeholderKey: 'target.placeholder.imap_mailboxes', helperKey: 'target.helper.imap_mailboxes', type: 'textarea' },
      { name: 'max_messages', labelKey: 'target.fields.imap_max_messages', placeholderKey: 'target.placeholder.imap_max_messages' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
  wordpress: {
    labelKey: 'target.type.wordpress',
    descriptionKey: 'target.type.wordpress.desc',
    fields: [
      { name: 'site_path', labelKey: 'target.fields.site_path', placeholderKey: 'target.placeholder.site_path', required: true },
      { name: 'db_host', labelKey: 'target.fields.db_host', placeholderKey: 'target.placeholder.db_host' },
      { name: 'db_port', labelKey: 'target.fields.db_port', placeholderKey: 'target.placeholder.db_port_mysql' },
      { name: 'db_socket', labelKey: 'target.fields.db_socket', placeholderKey: 'target.placeholder.db_socket_mysql' },
      { name: 'db_name', labelKey: 'target.fields.db_name', placeholderKey: 'target.placeholder.db_name', required: true },
      { name: 'db_user', labelKey: 'target.fields.db_user', placeholderKey: 'target.placeholder.db_user', required: true },
      { name: 'db_password', labelKey: 'target.fields.db_password', placeholderKey: 'target.placeholder.db_password', required: true, secret: true },
      { name: 'dump_options', labelKey: 'target.fields.dump_options', placeholderKey: 'target.placeholder.dump_options', helperKey: 'target.helper.dump_options', type: 'textarea' },
      { name: 'wp_cli_cmd', labelKey: 'target.fields.wp_cli_cmd', placeholderKey: 'target.placeholder.wp_cli_cmd', helperKey: 'target.helper.wp_cli_cmd' },
      { name: 'exclude_paths', labelKey: 'target.fields.exclude_paths', placeholderKey: 'target.placeholder.exclude_paths' },
      { name: 'extra_paths', labelKey: 'target.fields.extra_paths', placeholderKey: 'target.placeholder.extra_paths', helperKey: 'target.helper.extra_paths' },
      { name: 'tmp_dir', labelKey: 'target.fields.tmp_dir', placeholderKey: 'target.placeholder.tmp_dir' },
      { name: 'tags', labelKey: 'target.fields.tags', placeholderKey: 'target.placeholder.tags' },
      { name: 'note', labelKey: 'target.fields.note', placeholderKey: 'target.placeholder.note' },
    ],
  },
}

const ADVANCED_FIELDS: Record<string, string[]> = {
  m365_sharepoint: ['site_list', 'include_versions', 'max_versions', 'exclude_paths', 'include_lists', 'library_query', 'tmp_dir', 'tags', 'note'],
  m365_teams: ['team_list', 'include_private_channels', 'include_chats', 'include_meetings', 'max_messages', 'max_days', 'tmp_dir', 'tags', 'note'],
}

const emptyConfig = (targetType: string) => {
  const meta = TARGET_TYPES[targetType]
  if (!meta) return {}
  const base: Record<string, string> = {}
  meta.fields.forEach((field: FieldDef) => { base[field.name] = field.defaultValue ?? '' })
  return base
}

const findField = (targetType: string, fieldName: string) => TARGET_TYPES[targetType]?.fields.find((field: FieldDef) => field.name === fieldName)

export const summariseTarget = (target: Target, t: (key: string, params?: Record<string,string|number>) => string) => {
  const cfg = target.config || {}
  const entries: string[] = []

  const fallback = 'N/A'

  // Render condensed summary for Google Workspace targets when metadata is available.
  if (target.type === 'google_workspace') {
    const summary = cfg.workspace_summary as Record<string, any> | undefined
    if (summary && typeof summary === 'object') {
      const mode = String(summary.mode || fallback)
      const modules = Array.isArray(summary.modules) && summary.modules.length ? summary.modules.join(', ') : fallback
      const userCount = typeof summary.users_processed === 'number'
        ? summary.users_processed
        : Array.isArray(summary.users)
          ? summary.users.length
          : typeof summary.users_total === 'number'
            ? summary.users_total
            : undefined
      const usersLabel = userCount !== undefined ? String(userCount) : fallback
      entries.push(t('target.summary.google_workspace', { mode, modules, users: usersLabel }))
      const sampleUser = summary.sample_user || (Array.isArray(summary.sample_users) ? summary.sample_users[0] : undefined)
      if (sampleUser) {
        entries.push(t('target.summary.sample_user', { value: sampleUser }))
      }
      if (summary.group_email) {
        entries.push(t('target.summary.group', { value: summary.group_email }))
      }
      if (summary.admin_user) {
        entries.push(t('target.summary.admin', { value: summary.admin_user }))
      }
    }
  }

  // Render condensed summary for Microsoft 365 Exchange targets when metadata is available.
  if (target.type === 'm365_exchange') {
    const summary = cfg.exchange_summary as Record<string, any> | undefined
    if (summary && typeof summary === 'object') {
      const mode = String(summary.mode || fallback)
      const modules = Array.isArray(summary.modules) && summary.modules.length ? summary.modules.join(', ') : fallback
      const mailboxCount = typeof summary.mailboxes_total === 'number'
        ? summary.mailboxes_total
        : typeof summary.mailboxes === 'number'
          ? summary.mailboxes
        : Array.isArray(summary.mailboxes)
          ? summary.mailboxes.length
          : undefined
      const mailboxesLabel = mailboxCount !== undefined ? String(mailboxCount) : fallback
      entries.push(t('target.summary.m365_exchange', { mode, modules, mailboxes: mailboxesLabel }))
      const sampleMailbox = (() => {
        if (summary.sample_mailbox) return summary.sample_mailbox
        if (Array.isArray(summary.mailboxes_sample) && summary.mailboxes_sample.length) {
          return summary.mailboxes_sample.slice(0, 3).join(', ')
        }
        if (Array.isArray(summary.mailboxes) && summary.mailboxes.length && summary.mailboxes.length <= 3) {
          return summary.mailboxes.join(', ')
        }
        return undefined
      })()
      if (sampleMailbox) {
        entries.push(t('target.summary.sample_mailbox', { value: sampleMailbox }))
      }
      if (summary.admin_user) {
        entries.push(t('target.summary.admin', { value: summary.admin_user }))
      }
      if (summary.group_email) {
        entries.push(t('target.summary.group', { value: summary.group_email }))
      }
    }
  }

  if (target.type === 'm365_onedrive') {
    const summary = cfg.onedrive_summary as Record<string, any> | undefined
    if (summary && typeof summary === 'object') {
      const mode = String(summary.mode || fallback)
      const usersCount = typeof summary.users_processed === 'number'
        ? summary.users_processed
        : typeof summary.users_total === 'number'
          ? summary.users_total
          : Array.isArray(summary.users)
            ? summary.users.length
            : undefined
      const usersLabel = usersCount !== undefined ? usersCount.toLocaleString() : fallback
      entries.push(t('target.summary.m365_onedrive', { mode, users: usersLabel }))
      const sampleUser = (() => {
        if (summary.sample_user) return summary.sample_user
        if (Array.isArray(summary.sample_users) && summary.sample_users.length) return summary.sample_users[0]
        if (Array.isArray(summary.users) && summary.users.length && summary.users.length <= 3) return summary.users.join(', ')
        return undefined
      })()
      if (sampleUser) {
        entries.push(t('target.summary.sample_user', { value: String(sampleUser) }))
      }
      const filesCount = typeof summary.files_total === 'number'
        ? summary.files_total
        : typeof summary.files_exported === 'number'
          ? summary.files_exported
          : typeof summary.files_downloaded === 'number'
            ? summary.files_downloaded
            : undefined
      if (filesCount !== undefined) {
        entries.push(t('target.summary.onedrive_files', { value: filesCount.toLocaleString() }))
      }
      const bytesCount = typeof summary.bytes_total === 'number'
        ? summary.bytes_total
        : typeof summary.bytes_exported === 'number'
          ? summary.bytes_exported
          : typeof summary.bytes_downloaded === 'number'
            ? summary.bytes_downloaded
            : undefined
      if (bytesCount !== undefined) {
        entries.push(t('target.summary.onedrive_bytes', { value: bytesCount.toLocaleString() }))
      }
      if (typeof summary.include_shared === 'boolean') {
        const sharedLabel = t(summary.include_shared ? 'target.options.boolean.true' : 'target.options.boolean.false')
        entries.push(t('target.summary.onedrive_shared', { value: sharedLabel }))
      }
      const query = typeof summary.drive_query === 'string' ? summary.drive_query.trim() : ''
      if (query) {
        entries.push(t('target.summary.onedrive_query', { value: query }))
      }
      const excludesList = (() => {
        if (Array.isArray(summary.exclude_extensions)) return summary.exclude_extensions
        if (typeof summary.exclude_extensions === 'string') {
          return summary.exclude_extensions.split(/[\n,]/).map(item => item.trim()).filter(Boolean)
        }
        return [] as string[]
      })()
      if (excludesList.length) {
        entries.push(t('target.summary.onedrive_excludes', { value: excludesList.join(', ') }))
      }
      const maxFiles = typeof summary.max_files === 'number' ? summary.max_files : undefined
      if (maxFiles !== undefined) {
        entries.push(t('target.summary.onedrive_max_files', { value: maxFiles.toLocaleString() }))
      }
      const maxBytes = typeof summary.max_bytes === 'number' ? summary.max_bytes : undefined
      if (maxBytes !== undefined) {
        entries.push(t('target.summary.onedrive_max_bytes', { value: maxBytes.toLocaleString() }))
      }
    }
  }

  if (target.type === 'm365_sharepoint') {
    const summary = cfg.sharepoint_summary as Record<string, any> | undefined
    if (summary && typeof summary === 'object') {
      const sharepointMetrics = [
        { key: 'sites_total', labelKey: 'target.summary.sharepoint_sites' },
        { key: 'libraries_total', labelKey: 'target.summary.sharepoint_libraries' },
        { key: 'files_downloaded', labelKey: 'target.summary.sharepoint_files' },
        { key: 'bytes_downloaded', labelKey: 'target.summary.sharepoint_bytes' },
      ]
      sharepointMetrics.forEach(metric => {
        const rawValue = summary[metric.key]
        if (typeof rawValue === 'number') {
          entries.push(t(metric.labelKey, { value: rawValue.toLocaleString() }))
        }
      })
    }
  }

  if (target.type === 'm365_teams') {
    const summary = cfg.teams_summary as Record<string, any> | undefined
    if (summary && typeof summary === 'object') {
      const teamsMetrics = [
        { key: 'teams_processed', labelKey: 'target.summary.teams_processed' },
        { key: 'channels_processed', labelKey: 'target.summary.teams_channels' },
        { key: 'messages_archived', labelKey: 'target.summary.teams_messages' },
        { key: 'attachments_saved', labelKey: 'target.summary.teams_attachments' },
        { key: 'meetings_processed', labelKey: 'target.summary.teams_meetings' },
      ]
      teamsMetrics.forEach(metric => {
        const rawValue = summary[metric.key]
        if (typeof rawValue === 'number') {
          entries.push(t(metric.labelKey, { value: rawValue.toLocaleString() }))
        }
      })
    }
  }

  const hiddenKeys = new Set([
    'has_secrets',
    'workspace_summary',
    'workspace_state',
    'exchange_summary',
    'exchange_state',
    'onedrive_summary',
    'onedrive_state',
    'sharepoint_summary',
    'sharepoint_state',
    'teams_summary',
    'teams_state',
  ])

  Object.entries(cfg).forEach(([key, value]: [string, unknown]) => {
    if (hiddenKeys.has(key)) return
    const field = findField(target.type, key)
    const label = t(field?.labelKey || `target.fields.${key}`)
    const strVal = typeof value === 'string' ? value : JSON.stringify(value)
    entries.push(`${label}: ${strVal}`)
  })

  if ((cfg as any).has_secrets) entries.push(t('target.summary.secrets'))
  return entries.join(' | ')
}

export default function Targets({ endpointId: endpointIdProp }: { endpointId?: string }) {
  const { t } = useI18n()
  const params = useParams()
  const endpointId = endpointIdProp ?? (params.endpointId || '')
  const { has } = usePerms()
  const [items, setItems] = useState<Target[]>([])
  const [name, setName] = useState('nextcloud')
  const [type, setType] = useState<keyof typeof TARGET_TYPES>('nextcloud')
  const [configValues, setConfigValues] = useState<Record<string, string>>(emptyConfig('nextcloud'))
  const [includeCachePreviews, setIncludeCachePreviews] = useState(false)
  const [websiteDatabases, setWebsiteDatabases] = useState<WebsiteDatabaseForm[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [confirmId, setConfirmId] = useState<number | undefined>()
  const [editingId, setEditingId] = useState<number | undefined>()
  const [originalConfig, setOriginalConfig] = useState<Record<string, string>>({})
  const [originalName, setOriginalName] = useState('nextcloud')
  const [originalIncludeCache, setOriginalIncludeCache] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const meta = useMemo(() => TARGET_TYPES[type], [type])
  const isEditing = editingId !== undefined
  const allowCacheToggle = type === 'nextcloud'
  const isWebsite = type === 'website'
  const websiteMaintenanceEnabled = isWebsite && (configValues['maintenance_enabled'] || '').toLowerCase() === 'true'
  const advancedFieldNames = useMemo(() => ADVANCED_FIELDS[type] ?? [], [type])

  useEffect(() => {
    if (isEditing) {
      return
    }
    setConfigValues(emptyConfig(type))
    setWebsiteDatabases([])
    if (!allowCacheToggle) {
      setIncludeCachePreviews(false)
    }
  }, [type, allowCacheToggle, isEditing])

  useEffect(() => {
    if (isEditing) {
      return
    }
    if (isWebsite) {
      setConfigValues((prev: Record<string, string>) => {
        const next = { ...prev }
        if (!next.maintenance_enabled) {
          next.maintenance_enabled = 'false'
        }
        return next
      })
      setWebsiteDatabases((prev: WebsiteDatabaseForm[]) => (prev.length ? prev : [emptyWebsiteDb()]))
    } else {
      setWebsiteDatabases([])
    }
  }, [isWebsite, isEditing])

  const handleConfigChange = (fieldName: string) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { value } = event.target
    setConfigValues((prev: Record<string, string>) => ({ ...prev, [fieldName]: value }))
  }

  const resetForm = (fallbackType: keyof typeof TARGET_TYPES = 'nextcloud') => {
    setEditingId(undefined)
    setOriginalConfig({})
    setOriginalName(fallbackType)
    setOriginalIncludeCache(false)
    setType(fallbackType)
    setName(fallbackType)
    setConfigValues(emptyConfig(fallbackType))
    setIncludeCachePreviews(false)
    setWebsiteDatabases([])
    setShowAdvanced(false)
  }

  const cancelEdit = () => {
    resetForm()
  }

  const handleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newType = event.target.value as keyof typeof TARGET_TYPES
    const prevType = type
    setType(newType)
    setShowAdvanced(false)
    setName((prev: string) => {
      const trimmed = prev.trim()
      return trimmed === '' || trimmed === prevType ? newType : prev
    })
  }

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const handleCacheToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setIncludeCachePreviews(event.target.checked)
  }

  const handleWebsiteDbChange = (index: number, key: keyof WebsiteDatabaseForm) => (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { value } = event.target
    setWebsiteDatabases((prev: WebsiteDatabaseForm[]) => prev.map((entry: WebsiteDatabaseForm, idx: number) => (
      idx === index ? { ...entry, [key]: value } : entry
    )))
  }

  const addWebsiteDatabase = () => {
    setWebsiteDatabases((prev: WebsiteDatabaseForm[]) => [...prev, emptyWebsiteDb()])
  }

  const removeWebsiteDatabase = (index: number) => {
    setWebsiteDatabases((prev: WebsiteDatabaseForm[]) => prev.filter((_: WebsiteDatabaseForm, idx: number) => idx !== index))
  }

  const normalizeFieldValue = (field: FieldDef, raw: unknown): string => {
    if (raw === undefined || raw === null) {
      return ''
    }
    if (Array.isArray(raw)) {
      return raw.map(item => (typeof item === 'string' ? item : JSON.stringify(item))).join(',')
    }
    if (typeof raw === 'object') {
      try {
        return JSON.stringify(raw, null, 2)
      } catch {
        return String(raw)
      }
    }
    if (typeof raw === 'boolean') {
      return raw ? 'true' : 'false'
    }
    return String(raw)
  }

  const startEdit = (target: Target) => {
    if (!has('targets.write')) {
      return
    }
    const targetType = target.type as keyof typeof TARGET_TYPES
    const metaForTarget = TARGET_TYPES[targetType]
    const cleanedConfig: Record<string, any> = { ...(target.config || {}) }
    delete cleanedConfig.has_secrets
    delete cleanedConfig.databases_summary
    delete cleanedConfig.database_count
    const baseValues = emptyConfig(targetType)
    const nextValues: Record<string, string> = { ...baseValues }
    const originals: Record<string, string> = {}
    metaForTarget?.fields.forEach((field: FieldDef) => {
      if (field.type === 'databases') {
        return
      }
      const normalized = normalizeFieldValue(field, cleanedConfig[field.name])
      originals[field.name] = normalized
      nextValues[field.name] = field.secret ? '' : normalized
    })
    setEditingId(target.id)
    setType(targetType)
    setName(target.name)
    setOriginalName(target.name)
    const includeCache = Boolean(target.include_cache_previews)
    setIncludeCachePreviews(includeCache)
    setOriginalIncludeCache(includeCache)
    setConfigValues(nextValues)
    setOriginalConfig(originals)
    setWebsiteDatabases(targetType === 'website' ? [emptyWebsiteDb()] : [])
  const advancedKeys = ADVANCED_FIELDS[targetType] || []
    const hasAdvancedValues = advancedKeys.some((fieldName: string) => {
      const value = nextValues[fieldName] || ''
      return typeof value === 'string' ? value.trim() !== '' : false
    })
    setShowAdvanced(hasAdvancedValues)
  }

  const load = async () => {
    setLoading(true)
    try {
      if (!endpointId) return
      const data = await apiGet(`/endpoints/${endpointId}/targets`)
      setItems(data)
    } catch (e: any) {
      setError(e?.message || 'Load failed')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [endpointId])

  const submit = async () => {
    try {
      const trimmedName = name.trim()
      const editing = isEditing
      if (!trimmedName) { alert(t('target.errors.name_required')); return }
      if (!meta) { alert(t('target.errors.unsupported')); return }
      const resolveFieldValue = (field: FieldDef): string => {
        const raw = configValues[field.name] ?? ''
        if (field.trim === false) {
          return raw
        }
        return raw.trim()
      }
      const missing = meta.fields.filter((field: FieldDef) => {
        if (!field.required) return false
        if (field.type === 'databases') return false
        const value = resolveFieldValue(field)
        if (value) return false
        if (editing && field.secret) return false
        return true
      })
      if (missing.length) {
        const labels = missing.map((field: FieldDef) => t(field.labelKey)).join(', ')
        alert(t('target.errors.missing_fields', { fields: labels }))
        return
      }
      if (type === 'managed_db') {
        const provider = (configValues['provider'] || '').trim()
        const engineValue = (configValues['engine'] || '').trim()
        if (!provider) {
          alert(t('target.managed_db.errors.provider_required'))
          return
        }
        if (!engineValue) {
          alert(t('target.managed_db.errors.engine_required'))
          return
        }
        if (provider === 'aws_rds') {
          const missingAws: string[] = []
          if (!(configValues['region'] || '').trim()) missingAws.push(t('target.fields.region'))
          const accessKey = (configValues['aws_access_key_id'] || '').trim()
          const secretKey = (configValues['aws_secret_access_key'] || '').trim()
          if (!accessKey && !editing) missingAws.push(t('target.fields.aws_access_key_id'))
          if (!secretKey && !editing) missingAws.push(t('target.fields.aws_secret_access_key'))
          if (missingAws.length) {
            alert(t('target.managed_db.errors.aws_missing', { fields: missingAws.join(', ') }))
            return
          }
        }
        if (provider === 'gcp_cloudsql') {
          const missingGcp: string[] = []
          if (!(configValues['project_id'] || '').trim()) missingGcp.push(t('target.fields.project_id'))
          const serviceAccount = (configValues['gcp_service_account'] || '').trim()
          if (!serviceAccount && !editing) {
            missingGcp.push(t('target.fields.gcp_service_account'))
          }
          if (missingGcp.length) {
            alert(t('target.managed_db.errors.gcp_missing', { fields: missingGcp.join(', ') }))
            return
          }
          if (serviceAccount) {
            try {
              JSON.parse(serviceAccount)
            } catch (e) {
              alert(t('target.managed_db.errors.gcp_service_account_invalid'))
              return
            }
          }
        }
      }
      if (type === 's3') {
        const accessKey = (configValues['aws_access_key_id'] || '').trim()
        const secretKey = (configValues['aws_secret_access_key'] || '').trim()
        if ((accessKey && !secretKey) || (secretKey && !accessKey)) {
          alert(t('target.s3.errors.credentials_pair'))
          return
        }
        const objectLimitRaw = (configValues['object_limit'] || '').trim()
        if (objectLimitRaw) {
          const parsed = Number(objectLimitRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.s3.errors.object_limit'))
            return
          }
        }
        const maxBytesRaw = (configValues['max_total_bytes'] || '').trim()
        if (maxBytesRaw) {
          const parsed = Number(maxBytesRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.s3.errors.max_total_bytes'))
            return
          }
        }
      }
      if (type === 'm365_sharepoint') {
        const sitesRaw = (configValues['sites'] || '').trim()
        const siteListRaw = configValues['site_list'] || ''
        const siteListTrimmed = siteListRaw.trim()
        if (!editing && !sitesRaw && !siteListTrimmed) {
          alert(t('target.m365_sharepoint.errors.sites_required'))
          return
        }
        if (siteListTrimmed) {
          try {
            JSON.parse(siteListTrimmed)
          } catch {
            alert(t('target.m365_sharepoint.errors.site_list_invalid'))
            return
          }
        }
        const maxVersionsRaw = (configValues['max_versions'] || '').trim()
        if (maxVersionsRaw) {
          const parsed = Number(maxVersionsRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.m365_sharepoint.errors.max_versions'))
            return
          }
        }
      }
      if (type === 'm365_teams') {
        const teamsRaw = (configValues['teams'] || '').trim()
        const teamListRaw = configValues['team_list'] || ''
        const teamListTrimmed = teamListRaw.trim()
        if (teamListTrimmed) {
          try {
            JSON.parse(teamListTrimmed)
          } catch {
            alert(t('target.m365_teams.errors.team_list_invalid'))
            return
          }
        }
        const includeChatsValue = (configValues['include_chats'] || 'false').trim().toLowerCase()
        const includeMeetingsValue = (configValues['include_meetings'] || 'true').trim().toLowerCase()
        if (!editing) {
          const hasTeams = teamsRaw !== ''
          const hasTeamList = teamListTrimmed !== ''
          const chatsEnabled = includeChatsValue === 'true'
          const meetingsEnabled = includeMeetingsValue === 'true'
          if (!hasTeams && !hasTeamList && !chatsEnabled && !meetingsEnabled) {
            alert(t('target.m365_teams.errors.selection_required'))
            return
          }
        }
        const maxMessagesRaw = (configValues['max_messages'] || '').trim()
        if (maxMessagesRaw) {
          const parsed = Number(maxMessagesRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.m365_teams.errors.positive_integer', { field: t('target.fields.max_messages') }))
            return
          }
        }
        const maxDaysRaw = (configValues['max_days'] || '').trim()
        if (maxDaysRaw) {
          const parsed = Number(maxDaysRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.m365_teams.errors.positive_integer', { field: t('target.fields.max_days') }))
            return
          }
        }
      }
      let m365ExchangeNormalized: {
        mode: string
        modules?: string[]
        scopes?: string[]
        primaryUser?: string
        userList?: string
        groupEmail?: string
        adminUser?: string
        authorityHost?: string
        mailQuery?: string
        calendarQuery?: string
        maxParallel?: string
      } | null = null
      if (type === 'm365_exchange') {
        const mode = ((configValues['mode'] || '').trim().toLowerCase()) || 'single'
        if (!['single', 'list', 'group'].includes(mode)) {
          alert(t('target.m365_exchange.errors.mode_required'))
          return
        }
        const allowedModules = ['mail', 'calendar']
        const modulesRaw = (configValues['modules'] || '').trim()
        const normalizedModules: string[] = []
        if (modulesRaw) {
          const tokens = modulesRaw.split(/[\n,;]/)
          for (const token of tokens) {
            const trimmed = token.trim()
            if (!trimmed) continue
            const lower = trimmed.toLowerCase()
            if (!allowedModules.includes(lower)) {
              alert(t('target.m365_exchange.errors.modules_invalid', { value: trimmed }))
              return
            }
            if (!normalizedModules.includes(lower)) {
              normalizedModules.push(lower)
            }
          }
        }
        const scopesRaw = (configValues['scopes'] || '').trim()
        const normalizedScopes: string[] = []
        if (scopesRaw) {
          const tokens = scopesRaw.split(/[\n,;]/)
          for (const token of tokens) {
            const trimmed = token.trim()
            if (!trimmed) continue
            if (!normalizedScopes.includes(trimmed)) {
              normalizedScopes.push(trimmed)
            }
          }
        }
        const normalizeEmail = (value: string, labelKey: string, required: boolean): string | null => {
          const trimmed = (value || '').trim()
          if (!trimmed) {
            if (required) {
              alert(t('target.m365_exchange.errors.email_required', { field: t(labelKey) }))
              return null
            }
            return ''
          }
          if (!trimmed.includes('@')) {
            alert(t('target.m365_exchange.errors.email_invalid', { field: t(labelKey) }))
            return null
          }
          return trimmed.toLowerCase()
        }
        const primaryUser = normalizeEmail(configValues['primary_user'] || '', 'target.fields.primary_user', mode === 'single')
        if (primaryUser === null) return
        let normalizedUserList = ''
        if (mode === 'list') {
          const userListRaw = configValues['user_list'] || ''
          const tokens = userListRaw.replace(/;/g, '\n').split(/\n|,/)
          const collected: string[] = []
          for (const token of tokens) {
            const trimmed = token.trim()
            if (!trimmed) continue
            if (!trimmed.includes('@')) {
              alert(t('target.m365_exchange.errors.user_list_entry', { value: trimmed }))
              return
            }
            const lower = trimmed.toLowerCase()
            if (!collected.includes(lower)) {
              collected.push(lower)
            }
          }
          if (!collected.length) {
            alert(t('target.m365_exchange.errors.user_list_required'))
            return
          }
          normalizedUserList = collected.join('\n')
        }
        const groupEmail = normalizeEmail(configValues['group_email'] || '', 'target.fields.group_email', mode === 'group')
        if (groupEmail === null) return
        const adminUser = normalizeEmail(configValues['admin_user'] || '', 'target.fields.admin_user', mode === 'group')
        if (adminUser === null) return
        const maxParallelRaw = (configValues['max_parallel_exports'] || '').trim()
        if (maxParallelRaw) {
          const parsed = Number(maxParallelRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.m365_exchange.errors.positive_integer', { field: t('target.fields.max_parallel_exports') }))
            return
          }
        }
        const authorityHost = (configValues['authority_host'] || '').trim()
        const mailQuery = (configValues['mail_query'] || '').trim()
        const calendarQuery = (configValues['calendar_query'] || '').trim()
        m365ExchangeNormalized = {
          mode,
          modules: normalizedModules.length ? normalizedModules : undefined,
          scopes: normalizedScopes.length ? normalizedScopes : undefined,
          primaryUser: primaryUser || undefined,
          userList: mode === 'list' ? normalizedUserList : undefined,
          groupEmail: mode === 'group' ? groupEmail || undefined : undefined,
          adminUser: adminUser || undefined,
          authorityHost: authorityHost || undefined,
          mailQuery: mailQuery || undefined,
          calendarQuery: calendarQuery || undefined,
          maxParallel: maxParallelRaw || undefined,
        }
        if (mode === 'group' && !m365ExchangeNormalized.primaryUser) {
          m365ExchangeNormalized.primaryUser = m365ExchangeNormalized.adminUser || m365ExchangeNormalized.groupEmail
        }
      }
      let m365OnedriveNormalized: {
        mode: string
        includeShared: 'true' | 'false'
        primaryUser?: string
        userList?: string
        groupEmail?: string
        adminUser?: string
        driveQuery?: string
        maxFiles?: string
        maxBytes?: string
        excludeExtensions?: string
      } | null = null
      if (type === 'm365_onedrive') {
        const mode = ((configValues['mode'] || '').trim().toLowerCase()) || 'single'
        if (!['single', 'list', 'group'].includes(mode)) {
          alert(t('target.m365_onedrive.errors.mode_required'))
          return
        }
        const normalizeEmail = (value: string, labelKey: string, required: boolean): string | null => {
          const trimmed = (value || '').trim()
          if (!trimmed) {
            if (required) {
              alert(t('target.m365_onedrive.errors.email_required', { field: t(labelKey) }))
              return null
            }
            return ''
          }
          if (!trimmed.includes('@')) {
            alert(t('target.m365_onedrive.errors.email_invalid', { field: t(labelKey) }))
            return null
          }
          return trimmed.toLowerCase()
        }
        const primaryUser = normalizeEmail(configValues['primary_user'] || '', 'target.fields.primary_user', mode === 'single')
        if (primaryUser === null) return
        let normalizedUserList = ''
        if (mode === 'list') {
          const rawList = configValues['user_list'] || ''
          const tokens = rawList.replace(/;/g, '\n').split(/\n|,|\r/)
          const collected: string[] = []
          for (const token of tokens) {
            const trimmed = token.trim()
            if (!trimmed) continue
            if (!trimmed.includes('@')) {
              alert(t('target.m365_onedrive.errors.user_list_entry', { value: trimmed }))
              return
            }
            const lowered = trimmed.toLowerCase()
            if (!collected.includes(lowered)) {
              collected.push(lowered)
            }
          }
          if (!collected.length) {
            alert(t('target.m365_onedrive.errors.user_list_required'))
            return
          }
          normalizedUserList = collected.join('\n')
        }
        const groupEmail = normalizeEmail(configValues['group_email'] || '', 'target.fields.group_email', mode === 'group')
        if (groupEmail === null) return
        const adminUser = normalizeEmail(configValues['admin_user'] || '', 'target.fields.admin_user', mode === 'group')
        if (adminUser === null) return
        const includeSharedRaw = (configValues['include_shared'] || 'true').trim().toLowerCase()
        const includeShared: 'true' | 'false' = includeSharedRaw === 'false' ? 'false' : 'true'
        const driveQuery = (configValues['drive_query'] || '').trim()
        const maxFilesRaw = (configValues['max_files'] || '').trim()
        let maxFilesValue: string | undefined
        if (maxFilesRaw) {
          const parsed = Number(maxFilesRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.m365_onedrive.errors.positive_integer', { field: t('target.fields.onedrive_max_files') }))
            return
          }
          maxFilesValue = String(parsed)
        }
        const maxBytesRaw = (configValues['max_bytes'] || '').trim()
        let maxBytesValue: string | undefined
        if (maxBytesRaw) {
          const parsed = Number(maxBytesRaw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.m365_onedrive.errors.positive_integer', { field: t('target.fields.onedrive_max_bytes') }))
            return
          }
          maxBytesValue = String(parsed)
        }
        const excludeRaw = (configValues['exclude_extensions'] || '')
        const normalizedExcludes = (() => {
          if (!excludeRaw.trim()) return ''
          const tokens = excludeRaw.replace(/[,;]/g, '\n').split(/\n|\r/)
          const collected: string[] = []
          for (const token of tokens) {
            const cleaned = token.trim().toLowerCase().replace(/^\.+/, '')
            if (!cleaned) continue
            if (!collected.includes(cleaned)) {
              collected.push(cleaned)
            }
          }
          return collected.join('\n')
        })()
        m365OnedriveNormalized = {
          mode,
          includeShared,
          primaryUser: primaryUser || undefined,
          userList: mode === 'list' ? normalizedUserList : undefined,
          groupEmail: mode === 'group' ? (groupEmail || undefined) : undefined,
          adminUser: adminUser || undefined,
          driveQuery: driveQuery || undefined,
          maxFiles: maxFilesValue,
          maxBytes: maxBytesValue,
          excludeExtensions: normalizedExcludes || undefined,
        }
        if (mode === 'group' && !m365OnedriveNormalized.primaryUser) {
          m365OnedriveNormalized.primaryUser = m365OnedriveNormalized.adminUser || m365OnedriveNormalized.groupEmail
        }
      }
      let googleWorkspaceNormalized: {
        mode: string
        serviceAccount: string
        modules?: string[]
        primaryUser?: string
        userList?: string
        groupEmail?: string
        adminUser?: string
        driveIncludeShared: 'true' | 'false'
        numeric: Record<string, string>
        datetimes: Record<string, string>
      } | null = null
      if (type === 'google_workspace') {
        const mode = (configValues['mode'] || '').trim().toLowerCase()
        if (!mode || !['single', 'list', 'group'].includes(mode)) {
          alert(t('target.google_workspace.errors.mode_required'))
          return
        }
        const serviceAccount = (configValues['service_account_json'] || '').trim()
        if (!serviceAccount) {
          alert(t('target.google_workspace.errors.service_account'))
          return
        }
        try {
          JSON.parse(serviceAccount)
        } catch (err) {
          alert(t('target.google_workspace.errors.service_account_invalid'))
          return
        }
        const modulesRaw = (configValues['modules'] || '').trim()
        const allowedModules = ['drive', 'gmail', 'calendar']
        const normalizedModules: string[] = []
        if (modulesRaw) {
          const tokens = modulesRaw.split(/[\n,;]/)
          for (const token of tokens) {
            const trimmed = token.trim()
            if (!trimmed) continue
            const lower = trimmed.toLowerCase()
            if (!allowedModules.includes(lower)) {
              alert(t('target.google_workspace.errors.modules_invalid', { value: trimmed }))
              return
            }
            if (!normalizedModules.includes(lower)) {
              normalizedModules.push(lower)
            }
          }
          if (!normalizedModules.length) {
            alert(t('target.google_workspace.errors.modules_required'))
            return
          }
        }
        const normalizeEmail = (value: string, labelKey: string, required: boolean): string | null => {
          const trimmed = (value || '').trim()
          if (!trimmed) {
            if (required) {
              alert(t('target.google_workspace.errors.email_required', { field: t(labelKey) }))
              return null
            }
            return ''
          }
          if (!trimmed.includes('@')) {
            alert(t('target.google_workspace.errors.email_invalid', { field: t(labelKey) }))
            return null
          }
          return trimmed.toLowerCase()
        }
        const primaryUser = normalizeEmail(configValues['primary_user'] || '', 'target.fields.primary_user', mode === 'single')
        if (primaryUser === null) return
        let normalizedUserList = ''
        if (mode === 'list') {
          const userListRaw = (configValues['user_list'] || '')
          const tokens = userListRaw.replace(/;/g, '\n').split(/\n|,/)
          const collected: string[] = []
          for (const token of tokens) {
            const trimmed = token.trim()
            if (!trimmed) continue
            if (!trimmed.includes('@')) {
              alert(t('target.google_workspace.errors.user_list_entry', { value: trimmed }))
              return
            }
            const lower = trimmed.toLowerCase()
            if (!collected.includes(lower)) {
              collected.push(lower)
            }
          }
          if (!collected.length) {
            alert(t('target.google_workspace.errors.user_list_required'))
            return
          }
          normalizedUserList = collected.join('\n')
        }
        const groupEmail = normalizeEmail(configValues['group_email'] || '', 'target.fields.group_email', mode === 'group')
        if (groupEmail === null) return
        const adminUser = normalizeEmail(configValues['admin_user'] || '', 'target.fields.admin_user', mode === 'group')
        if (adminUser === null) return
        const driveIncludeRaw = (configValues['drive_include_shared'] || 'true').trim().toLowerCase()
        const driveIncludeShared = driveIncludeRaw === 'false' ? 'false' : 'true'
        const numericFields: Array<{ key: string; label: string }> = [
          { key: 'drive_max_files', label: t('target.fields.drive_max_files') },
          { key: 'gmail_max_messages', label: t('target.fields.gmail_max_messages') },
          { key: 'calendar_max_events', label: t('target.fields.calendar_max_events') },
        ]
        const numericValues: Record<string, string> = {}
        for (const field of numericFields) {
          const raw = (configValues[field.key] || '').trim()
          if (!raw) {
            continue
          }
          const parsed = Number(raw)
          if (!Number.isInteger(parsed) || parsed <= 0) {
            alert(t('target.google_workspace.errors.positive_integer', { field: field.label }))
            return
          }
          numericValues[field.key] = String(parsed)
        }
        const datetimeFields: Array<{ key: string; label: string }> = [
          { key: 'calendar_time_min', label: t('target.fields.calendar_time_min') },
          { key: 'calendar_time_max', label: t('target.fields.calendar_time_max') },
        ]
        const datetimeValues: Record<string, string> = {}
        for (const field of datetimeFields) {
          const raw = (configValues[field.key] || '').trim()
          if (!raw) {
            continue
          }
          if (Number.isNaN(Date.parse(raw))) {
            alert(t('target.google_workspace.errors.datetime', { field: field.label }))
            return
          }
          datetimeValues[field.key] = raw
        }
        googleWorkspaceNormalized = {
          mode,
          serviceAccount,
          modules: normalizedModules.length ? normalizedModules : undefined,
          primaryUser: primaryUser || undefined,
          userList: mode === 'list' ? normalizedUserList : undefined,
          groupEmail: mode === 'group' ? groupEmail || undefined : undefined,
          adminUser: adminUser || undefined,
          driveIncludeShared,
          numeric: numericValues,
          datetimes: datetimeValues,
        }
        if (mode === 'group' && !googleWorkspaceNormalized.primaryUser) {
          googleWorkspaceNormalized.primaryUser = googleWorkspaceNormalized.adminUser || googleWorkspaceNormalized.groupEmail
        }
      }
      const maintenanceEnabled = websiteMaintenanceEnabled
      const normalizedWebsiteDbs = websiteDatabases
        .map((db: WebsiteDatabaseForm): Record<string, string> | null => {
          const baseType = db.type
          const name = db.db_name.trim()
          const user = db.db_user.trim()
          const password = db.db_password
          if (!baseType || !name || !user || !password) {
            return null
          }
          const payloadDb: Record<string, string> = {
            type: baseType,
            db_name: name,
            db_user: user,
            db_password: password,
          }
          const host = db.db_host.trim()
          const port = db.db_port.trim()
          const socket = db.db_socket.trim()
          const dumpOptions = db.dump_options.trim()
          if (host) payloadDb.db_host = host
          if (port) payloadDb.db_port = port
          if (socket) payloadDb.db_socket = socket
          if (dumpOptions) payloadDb.dump_options = dumpOptions
          return payloadDb
        })
        .filter((db: Record<string, string> | null): db is Record<string, string> => db !== null)
      if (isWebsite) {
        if (maintenanceEnabled) {
          const onCmd = (configValues['maintenance_on_cmd'] || '').trim()
          const offCmd = (configValues['maintenance_off_cmd'] || '').trim()
          if (!onCmd || !offCmd) {
            alert(t('target.errors.missing_fields', { fields: `${t('target.fields.maintenance_on_cmd')}, ${t('target.fields.maintenance_off_cmd')}` }))
            return
          }
        }
        if (!normalizedWebsiteDbs.length) {
          const fallbackName = (configValues['db_name'] || '').trim()
          const fallbackUser = (configValues['db_user'] || '').trim()
          const fallbackPass = (configValues['db_password'] || '').trim()
          if (!fallbackName || !fallbackUser || !fallbackPass) {
            if (!editing) {
              alert(t('target.website.errors.database_required'))
              return
            }
          }
        }
      }
      const payload: Record<string, string> = {}
      const configDiff: Record<string, string | null> = {}
      meta.fields.forEach((field: FieldDef) => {
        if (field.type === 'databases') {
          return
        }
        const value = resolveFieldValue(field)
        if (!editing) {
          if (value) payload[field.name] = value
        } else {
          const originalValue = originalConfig[field.name] ?? ''
          if (field.secret && !value) {
            return
          }
          if (value) {
            if (value !== originalValue) {
              configDiff[field.name] = value
            }
          } else if (originalValue) {
            configDiff[field.name] = null
          }
        }
      })
      const assignConfigValue = (key: string, nextValue: string | null | undefined, opts: { secret?: boolean } = {}) => {
        if (!editing) {
          if (nextValue === null || nextValue === undefined) {
            delete payload[key]
            return
          }
          if (nextValue !== '') {
            payload[key] = nextValue
          } else {
            delete payload[key]
          }
          return
        }
        const originalValue = originalConfig[key] ?? ''
        const isSecret = opts.secret ?? false
        if (nextValue === undefined) {
          return
        }
        if (nextValue === null) {
          if (originalValue) {
            configDiff[key] = null
          }
          return
        }
        const normalized = String(nextValue)
        if (isSecret && !normalized.trim()) {
          return
        }
        if (!normalized && originalValue) {
          configDiff[key] = null
        } else if (normalized && normalized !== originalValue) {
          configDiff[key] = normalized
        }
      }
      if (type === 'm365_exchange' && m365ExchangeNormalized) {
        assignConfigValue('mode', m365ExchangeNormalized.mode)
        assignConfigValue('modules', m365ExchangeNormalized.modules ? m365ExchangeNormalized.modules.join(',') : null)
        assignConfigValue('scopes', m365ExchangeNormalized.scopes ? m365ExchangeNormalized.scopes.join(',') : null)
        assignConfigValue('primary_user', m365ExchangeNormalized.primaryUser || null)
        assignConfigValue('user_list', m365ExchangeNormalized.userList || null)
        assignConfigValue('group_email', m365ExchangeNormalized.groupEmail || null)
        assignConfigValue('admin_user', m365ExchangeNormalized.adminUser || null)
        assignConfigValue('authority_host', m365ExchangeNormalized.authorityHost || null)
        assignConfigValue('mail_query', m365ExchangeNormalized.mailQuery || null)
        assignConfigValue('calendar_query', m365ExchangeNormalized.calendarQuery || null)
        assignConfigValue('max_parallel_exports', m365ExchangeNormalized.maxParallel || null)
      }
      if (type === 'm365_onedrive' && m365OnedriveNormalized) {
        assignConfigValue('mode', m365OnedriveNormalized.mode)
        assignConfigValue('primary_user', m365OnedriveNormalized.primaryUser || null)
        assignConfigValue('user_list', m365OnedriveNormalized.userList || null)
        assignConfigValue('group_email', m365OnedriveNormalized.groupEmail || null)
        assignConfigValue('admin_user', m365OnedriveNormalized.adminUser || null)
        assignConfigValue('include_shared', m365OnedriveNormalized.includeShared)
        assignConfigValue('drive_query', m365OnedriveNormalized.driveQuery || null)
        assignConfigValue('max_files', m365OnedriveNormalized.maxFiles || null)
        assignConfigValue('max_bytes', m365OnedriveNormalized.maxBytes || null)
        assignConfigValue('exclude_extensions', m365OnedriveNormalized.excludeExtensions || null)
      }
      if (type === 'google_workspace' && googleWorkspaceNormalized) {
        assignConfigValue('mode', googleWorkspaceNormalized.mode)
        assignConfigValue('service_account_json', googleWorkspaceNormalized.serviceAccount || (editing ? undefined : ''), { secret: true })
        assignConfigValue('modules', googleWorkspaceNormalized.modules ? googleWorkspaceNormalized.modules.join(',') : null)
        assignConfigValue('drive_include_shared', googleWorkspaceNormalized.driveIncludeShared)
        assignConfigValue('primary_user', googleWorkspaceNormalized.primaryUser || null)
        assignConfigValue('user_list', googleWorkspaceNormalized.userList || null)
        assignConfigValue('group_email', googleWorkspaceNormalized.groupEmail || null)
        assignConfigValue('admin_user', googleWorkspaceNormalized.adminUser || null)
        Object.entries(googleWorkspaceNormalized.numeric).forEach(([key, value]) => {
          assignConfigValue(key, value)
        })
        Object.entries(googleWorkspaceNormalized.datetimes).forEach(([key, value]) => {
          assignConfigValue(key, value)
        })
      }
      if (type === 'git') {
        const method = (configValues['auth_method'] || 'auto').trim().toLowerCase() || 'auto'
        const token = (configValues['token'] || '').trim()
        const tokenUser = (configValues['token_username'] || '').trim()
        const username = (configValues['username'] || '').trim()
        const password = (configValues['password'] || '').trim()
        const sshKey = (configValues['ssh_private_key'] || '').trim()
        if (method === 'token' && !token && !editing) {
          alert(t('target.git.errors.token_required'))
          return
        }
        if (method === 'token' && tokenUser) {
          assignConfigValue('token_username', tokenUser)
        }
        if (method === 'basic' && (!username || !password) && !editing) {
          alert(t('target.git.errors.basic_required'))
          return
        }
        if (method === 'ssh' && !sshKey && !editing) {
          alert(t('target.git.errors.ssh_required'))
          return
        }
      }
      if (isWebsite) {
        assignConfigValue('maintenance_enabled', maintenanceEnabled ? 'true' : 'false')
        if (normalizedWebsiteDbs.length) {
          const serialized = JSON.stringify(normalizedWebsiteDbs)
          assignConfigValue('databases', serialized)
        }
      }
      if (!editing) {
        await apiPost(`/endpoints/${endpointId}/targets`, {
          name: trimmedName,
          type,
          config: payload,
          include_cache_previews: allowCacheToggle && includeCachePreviews,
        })
        setName(type)
        setConfigValues(emptyConfig(type))
        setIncludeCachePreviews(allowCacheToggle ? includeCachePreviews : false)
        setWebsiteDatabases(isWebsite ? [emptyWebsiteDb()] : [])
      } else {
        const body: Record<string, unknown> = {}
        if (trimmedName !== originalName) {
          body.name = trimmedName
        }
        if (allowCacheToggle && includeCachePreviews !== originalIncludeCache) {
          body.include_cache_previews = includeCachePreviews
        }
        const diffEntries = Object.entries(configDiff)
        if (diffEntries.length) {
          const sanitizedDiff: Record<string, unknown> = {}
          diffEntries.forEach(([key, val]) => {
            sanitizedDiff[key] = val
          })
          body.config = sanitizedDiff
        }
        if (!body.name && body.include_cache_previews === undefined && !body.config) {
          alert(t('target.feedback.no_changes'))
          return
        }
        await apiPut(`/targets/${editingId}`, body)
        resetForm()
      }
      await load()
    } catch (e: any) {
      alert(e?.message || t('target.errors.create_failed'))
    }
  }

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{t('titles.targets_for', { id: String(endpointId) })}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('help.targets')}</p>
      {isEditing && (
        <div className="mb-4 rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 dark:bg-blue-900/20 dark:text-blue-100">
          {t('target.feedback.edit_mode', { name: originalName })}
        </div>
      )}
      <div className="grid gap-4">
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('table.type')}</span>
            <select
              className="input"
              value={type}
              onChange={handleTypeChange}
              disabled={!has('targets.write') || isEditing}
            >
              {Object.entries(TARGET_TYPES).map(([value, metaDef]) => (
                <option key={value} value={value}>{t(metaDef.labelKey)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('table.name')}</span>
            <input className="input" placeholder={t('ph.name')} value={name} onChange={handleNameChange} disabled={!has('targets.write')} />
          </label>
          {allowCacheToggle ? (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={includeCachePreviews} onChange={handleCacheToggle} disabled={!has('targets.write')} />
              <span>{t('target.fields.include_cache')}</span>
            </label>
          ) : (
            <div />
          )}
          <button className="btn h-11 md:col-span-3" onClick={submit} disabled={!has('targets.write')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6Z"/></svg>
            {isEditing ? t('buttons.save') : t('buttons.add')}
          </button>
          {isEditing && (
            <button className="btn-secondary h-11 md:col-span-3" onClick={cancelEdit} disabled={!has('targets.write')}>
              {t('buttons.cancel')}
            </button>
          )}
        </div>
        {meta && (
          <div className="bg-gray-50 dark:bg-gray-900/40 rounded p-3 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t(meta.descriptionKey)}</p>
            {(() => {
              if (!advancedFieldNames.length) return null
              const hasAdvancedValues = advancedFieldNames.some((fieldName: string) => {
                const raw = configValues[fieldName]
                if (!raw) return false
                if (typeof raw !== 'string') return true
                return raw.trim() !== ''
              })
              return (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                  {!showAdvanced && hasAdvancedValues && (
                    <span className="text-xs text-amber-600 dark:text-amber-300">{t('target.form.advanced_populated')}</span>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      onClick={() => setShowAdvanced(prev => !prev)}
                    >
                      {showAdvanced ? t('target.form.hide_advanced') : t('target.form.show_advanced')}
                    </button>
                  </div>
                </div>
              )
            })()}
            <div className="grid md:grid-cols-2 gap-3">
              {meta.fields.map((field: FieldDef) => {
                if (!showAdvanced && advancedFieldNames.includes(field.name)) {
                  return null
                }
                const value = configValues[field.name] || ''
                const placeholder = field.placeholderKey ? t(field.placeholderKey) : ''
                const disabled = !has('targets.write')
                const maintenanceCommand = isWebsite && (field.name === 'maintenance_on_cmd' || field.name === 'maintenance_off_cmd')
                const fieldDisabled = disabled || (maintenanceCommand && !websiteMaintenanceEnabled)
                const commonProps = {
                  placeholder,
                  value,
                  onChange: handleConfigChange(field.name),
                  className: 'input',
                  disabled: fieldDisabled,
                }
                return (
                  <label key={field.name} className={`flex flex-col text-sm ${field.type === 'databases' ? 'md:col-span-2' : ''}`}>
                    <span className="mb-1 font-medium">
                      {t(field.labelKey)}{field.required && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    {field.type === 'textarea' ? (
                      <textarea {...commonProps} rows={3} />
                    ) : field.type === 'select' && field.options ? (
                      <select {...commonProps}>
                        <option value="">{t('target.placeholder.select')}</option>
                        {field.options.map(opt => (
                          <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                        ))}
                      </select>
                    ) : field.type === 'databases' ? (
                      <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">{t('target.website.database_hint')}</span>
                          {has('targets.write') && (
                            <button type="button" className="btn-secondary" onClick={addWebsiteDatabase} disabled={disabled}>
                              {t('target.website.buttons.add_db')}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-col gap-3">
                          {websiteDatabases.map((db, idx) => (
                            <div key={`${idx}-${db.db_name || 'db'}`} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                              <div className="flex justify-between items-center mb-3">
                                <span className="font-medium text-sm">{t('target.website.database_label', { index: idx + 1 })}</span>
                                {has('targets.write') && websiteDatabases.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => removeWebsiteDatabase(idx)}
                                    disabled={disabled}
                                  >
                                    {t('target.website.buttons.remove_db')}
                                  </button>
                                )}
                              </div>
                              <div className="grid md:grid-cols-2 gap-3">
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_type')}*</span>
                                  <select
                                    className="input"
                                    value={db.type}
                                    onChange={handleWebsiteDbChange(idx, 'type')}
                                    disabled={disabled}
                                  >
                                    <option value="">{t('target.placeholder.select')}</option>
                                    <option value="mysql">{t('target.options.db_type.mysql')}</option>
                                    <option value="postgres">{t('target.options.db_type.postgres')}</option>
                                  </select>
                                </label>
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_name')}*</span>
                                  <input
                                    className="input"
                                    value={db.db_name}
                                    onChange={handleWebsiteDbChange(idx, 'db_name')}
                                    placeholder={t('target.placeholder.db_name')}
                                    disabled={disabled}
                                  />
                                </label>
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_user')}*</span>
                                  <input
                                    className="input"
                                    value={db.db_user}
                                    onChange={handleWebsiteDbChange(idx, 'db_user')}
                                    placeholder={t('target.placeholder.db_user')}
                                    disabled={disabled}
                                  />
                                </label>
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_password')}*</span>
                                  <input
                                    className="input"
                                    type="password"
                                    value={db.db_password}
                                    onChange={handleWebsiteDbChange(idx, 'db_password')}
                                    placeholder={t('target.placeholder.db_password')}
                                    disabled={disabled}
                                  />
                                </label>
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_host')}</span>
                                  <input
                                    className="input"
                                    value={db.db_host}
                                    onChange={handleWebsiteDbChange(idx, 'db_host')}
                                    placeholder={t('target.placeholder.db_host')}
                                    disabled={disabled}
                                  />
                                </label>
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_port')}</span>
                                  <input
                                    className="input"
                                    value={db.db_port}
                                    onChange={handleWebsiteDbChange(idx, 'db_port')}
                                    placeholder={t('target.placeholder.db_port')}
                                    disabled={disabled}
                                  />
                                </label>
                                <label className="flex flex-col text-xs">
                                  <span className="mb-1 font-semibold">{t('target.fields.db_socket')}</span>
                                  <input
                                    className="input"
                                    value={db.db_socket}
                                    onChange={handleWebsiteDbChange(idx, 'db_socket')}
                                    placeholder={t('target.placeholder.db_socket')}
                                    disabled={disabled}
                                  />
                                </label>
                                <label className="flex flex-col text-xs md:col-span-2">
                                  <span className="mb-1 font-semibold">{t('target.fields.dump_options')}</span>
                                  <textarea
                                    className="input"
                                    rows={2}
                                    value={db.dump_options}
                                    onChange={handleWebsiteDbChange(idx, 'dump_options')}
                                    placeholder={t('target.placeholder.dump_options')}
                                    disabled={disabled}
                                  />
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <input
                        {...commonProps}
                        type={field.secret ? 'password' : 'text'}
                      />
                    )}
                    {field.secret && isEditing && <span className="text-xs text-blue-600 dark:text-blue-300 mt-1">{t('target.feedback.secret_hint')}</span>}
                    {field.type !== 'databases' && field.helperKey && <span className="text-xs text-gray-500 mt-1">{t(field.helperKey)}</span>}
                  </label>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {!has('targets.write') && <p className="text-xs text-gray-500 mb-2">{t('target.feedback.no_permission')}</p>}
      {loading ? <p>{t('common.loading')}</p> : error ? <p className="text-red-600">{error}</p> : (
        <table className="w-full text-sm mt-6">
          <thead><tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"><th className="text-left">{t('table.id')}</th><th className="text-left">{t('table.name')}</th><th>{t('table.type')}</th><th className="text-left">{t('target.table.config')}</th><th></th></tr></thead>
          <tbody>
            {items.map(tgt => (
              <tr key={tgt.id} className="border-t border-gray-200 dark:border-gray-700">
                <td>{tgt.id}</td>
                <td>{tgt.name}</td>
                <td className="text-center">{t(TARGET_TYPES[tgt.type]?.labelKey || tgt.type)}</td>
                <td>{summariseTarget(tgt, t) || '-'}</td>
                <td className="text-right">
                  {has('targets.write') && (
                    <div className="flex justify-end gap-2">
                      <button className="link" onClick={() => startEdit(tgt)}>
                        {t('buttons.edit')}
                      </button>
                      <button className="btn-secondary" onClick={() => setConfirmId(tgt.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M7 6h10l-1 14H8L7 6Zm2-2h6l1 2H8l1-2Z"/></svg>
                    {t('buttons.delete')}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Confirm open={!!confirmId} onClose={() => setConfirmId(undefined)} onConfirm={async () => {
        if (!confirmId) return
        try {
          await fetch(`/targets/${confirmId}`, { method: 'DELETE', credentials: 'include' })
          await load()
        } finally {
          setConfirmId(undefined)
        }
      }}>
        {t('target.feedback.confirm_delete')}
      </Confirm>
    </div>
  )
}
